import { type EnrichedTracker, EnrichedTrackerSchema } from '@racemap/sdk/schema/trackers';
import { UserOverviewSchema } from '@racemap/sdk/schema/user';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { checkEnv } from '@racemap/utilities/functions/environmentals';
import { sleep } from '@racemap/utilities/functions/utils';
import { produce } from 'immer';
import { DateTime } from 'luxon';
import { type ToastId, toast } from 'src/lib/toast';
import type { StoreApi } from 'zustand';
import { getRentalGroupCounts } from '../../../components/TrackerManager/Filter/FilterTreeFunctions';
import type { DraftState, State } from '../../reducers';
import {
  fetchUsers,
  getAllUserIDsFromTracker,
  getStaleRentalFilters,
  getUserIdsFromFilterSet,
  isNewerTracker,
  prepareMessageTemplates,
  updateUsersWithRoleAndTrackers,
} from '../trackers_helper';
import type { TrackersActions } from '../trackers_reducers';

const apiClient = RacemapAPIClient.fromWindowLocation();

const racemapDeviceUserId = checkEnv(import.meta.env.VITE_RM_DEVICE_USER, 'VITE_RM_DEVICE_USER');

export const createLoadDataActions = (
  set: StoreApi<State>['setState'],
  get: StoreApi<State>['getState'],
): Pick<TrackersActions, 'loadData'> => ({
  loadData: async () => {
    if (get().trackers.loadingState === 'LOADING') return;
    let toastId: ToastId | undefined;

    try {
      const startLoading = new Date();
      const isAllTrackerLoad = get().trackers.lastTrackerLoadedAt == null;
      if (isAllTrackerLoad) {
        toastId = toast('Loading trackers', {
          autoClose: false,
          isLoading: true,
          progress: 0,
          type: 'info',
          closeButton: false,
          draggable: false,
          position: 'top-center',
        });

        const currentUser = get().users.getter.currentUser();
        if (currentUser?.admin && get().trackers.users.size === 0 && racemapDeviceUserId) {
          set(
            produce((s: DraftState) => {
              const userPath = `/users/${racemapDeviceUserId}`;
              [`${userPath}/owner`, `${userPath}/editor`, `${userPath}/borrower`].forEach((role) =>
                s.trackers.filterSet.add(role),
              );

              const currentUserPath = `/users/${currentUser.id}`;
              [
                `${currentUserPath}/owner`,
                `${currentUserPath}/editor`,
                `${currentUserPath}/borrower`,
              ].forEach((role) => s.trackers.filterSet.add(role));
            }),
          );
        }
      }
      set(
        produce((s: DraftState) => {
          s.trackers.loadingState = 'LOADING';
        }),
      );

      const filteredUserIds = Array.from(getUserIdsFromFilterSet(get().trackers.filterSet));

      const [trackers, events, tags, newUsers, templates] = await Promise.all([
        apiClient.listTrackers(
          {
            show: 'archived',
            updatedSince: get().trackers.lastTrackerLoadedAt,
            userIds: filteredUserIds,
          },
          {
            validate: false,
          },
        ),
        apiClient.getEvents({
          filter: 'can-edit',
          show: ['hidden'],
          endingAfter: DateTime.now().startOf('day').minus({ days: 14 }).toJSDate(),
        }),
        apiClient.listTrackerTags(),
        fetchUsers(get().trackers.userSearchValue, get().trackers.filterSet),
        apiClient.listMessageTemplates(),
      ]);

      if (toastId != null) {
        toast.update(toastId, {
          render: 'Prepare trackers (0%)',
          progress: 0.2,
        });
      }

      const preparedMessageTemplates = prepareMessageTemplates(templates);

      set(
        produce((s: DraftState) => {
          s.trackers.events = new Map(events.map((e) => [e.id, e]));
          s.trackers.trackerTags = new Map(tags.map((tT) => [tT.id.toHexString(), tT]));
          s.trackers.lastTrackerLoadedAt = startLoading;
          s.trackers.messageTemplates = preparedMessageTemplates;
        }),
      );

      const trackerParsed: Array<EnrichedTracker> = [];
      let i = 0;
      let lastPercent = -1;

      const allUserIDsFromTrackers = new Set<string>();

      for (const trackerRaw of trackers) {
        trackerParsed.push(await EnrichedTrackerSchema.parseAsync(trackerRaw));
        i++;

        if (i % 100 === 0 || i === trackers.length) {
          set(
            produce((s: DraftState) => {
              for (const tracker of trackerParsed) {
                // Skip stale snapshots so a slow poll cannot overwrite fresher
                // items written by a mutation refetch (e.g. clear/revoke).
                if (!isNewerTracker(tracker, s.trackers.items.get(tracker.id.toHexString()))) {
                  continue;
                }
                s.trackers.items.set(tracker.id.toHexString(), tracker);
                updateUsersWithRoleAndTrackers(tracker, s.trackers.usersWithRoleAndTrackers);
                const userIDsFromTracker = getAllUserIDsFromTracker(tracker);
                userIDsFromTracker.forEach((userID) => {
                  allUserIDsFromTrackers.add(userID);
                });
              }
            }),
          );
          trackerParsed.length = 0;
        }

        if (i % 10 === 0) {
          await sleep(0);

          const percent = Math.floor((i / trackers.length) * 100);
          if (lastPercent !== percent && percent % 10 === 0) {
            if (toastId != null) {
              toast.update(toastId, {
                render: `Prepare trackers (${percent}%)`,
                progress: 0.2 + percent * (6 / 1000),
              });
            }
            lastPercent = percent;
          }
        }
      }

      set(
        produce((s: DraftState) => {
          s.trackers.users = newUsers;
          const currentUser = get().users.getter.currentUser();

          if (
            racemapDeviceUserId != null &&
            currentUser?.admin &&
            s.trackers.users.get(racemapDeviceUserId) == null
          ) {
            s.trackers.users.set(
              racemapDeviceUserId,
              UserOverviewSchema.parse({
                id: racemapDeviceUserId,
                name: 'Devices@Racemap',
                email: 'devices@racemap.com',
                parentId: null,
              }),
            );
          }
        }),
      );

      if (toastId != null) {
        toast.update(toastId, {
          render: 'Calculate filter counts',
          progress: 0.8,
        });
      }

      const tool = get().trackers.filterTreePreparation;
      const trackersArray = Array.from(get().trackers.items.values());

      // ahead of the worker, so the rental groups do not depend on it being available
      const rentalGroups = getRentalGroupCounts(trackersArray, get().users.currentUserId);
      const staleRentalFilters = getStaleRentalFilters(get().trackers.filterSet, rentalGroups);
      set(
        produce((s: DraftState) => {
          s.trackers.rentalGroups = rentalGroups;
          // a rental node vanishes when its borrow period ends; left in the filter set its
          // key would match nothing and silently empty the table
          for (const stale of staleRentalFilters) s.trackers.filterSet.delete(stale);
        }),
      );

      const options = {
        events,
        trackers: trackersArray,
        tags,
        filteredUserIds,
        currentUserId: get().users.currentUserId,
      };

      const filterCounts =
        (await tool?.calculateFilterCounts(JSON.stringify(options))) || new Map();
      set(
        produce((s: DraftState) => {
          s.trackers.filterCounts = filterCounts;
          s.trackers.loadingState = 'LOADED';
        }),
      );

      if (toastId != null) {
        toast.update(toastId, {
          render: 'Successfully loaded trackers',
          isLoading: false,
          progress: null,
          hideProgressBar: true,
          autoClose: 2 * OneSecondInMillis,
          type: 'success',
        });
      }
    } catch (err) {
      if (err instanceof Error) {
        console.error(err);

        if (toastId != null) {
          toast.update(toastId, {
            render: `Failed to load trackers: ${err.message}`,
            isLoading: false,
            progress: null,
            hideProgressBar: true,
            autoClose: 2 * OneSecondInMillis,
            type: 'error',
          });
        } else {
          toast.error(`Failed to load trackers: ${err.message}`);
        }
      }

      set(
        produce((s: DraftState) => {
          s.trackers.loadingState = 'ERROR';
          // reset the last loaded at time to force full reload next time
          s.trackers.lastTrackerLoadedAt = undefined;
        }),
      );
    }
  },
});
